import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Send, MessageCircle } from "lucide-react";
import { api } from "../lib/api";

// Two-way chat between rider and chauffeur. Polls every 5s.
export function ChatPanel({ listUrl, sendUrl, sendBase = {}, me, title = "Chat", testPrefix = "chat", quickReplies = [] }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(listUrl); setMsgs(data); } catch { /* ignore */ }
  }, [listUrl]);

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current && endRef.current.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const sendText = async (raw) => {
    const t = (raw ?? "").trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(sendUrl, { ...sendBase, text: t });
      setText("");
      load();
    } catch { toast.error("Couldn't send message"); }
    finally { setSending(false); }
  };
  const send = () => sendText(text);

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div className="border border-white/10 bg-[var(--nl-bg-2)]" data-testid={`${testPrefix}-panel`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
        <MessageCircle size={15} className="text-gold" />
        <p className="label text-white/70 text-xs">{title}</p>
      </div>
      <div className="h-52 overflow-y-auto px-4 py-3 space-y-2" data-testid={`${testPrefix}-messages`}>
        {msgs.length === 0 ? (
          <p className="text-white/35 font-body text-xs text-center py-8">No messages yet. Say hello 👋</p>
        ) : msgs.map((m) => {
          const mine = m.sender === me;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-3 py-2 font-body text-sm font-medium leading-snug ${mine ? "bg-gold text-black" : "bg-[var(--nl-bg-3)] text-white border border-white/10"}`}>
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>
      {quickReplies.length > 0 && (
        <div className="flex gap-2 overflow-x-auto px-3 pt-3 pb-1 no-scrollbar" data-testid={`${testPrefix}-quick`}>
          {quickReplies.map((q) => (
            <button
              key={q}
              onClick={() => sendText(q)}
              disabled={sending}
              className="label text-[11px] whitespace-nowrap border border-gold/40 text-gold px-3 py-1.5 hover:bg-gold/10 transition-colors duration-300 disabled:opacity-50"
              data-testid={`${testPrefix}-quick-${q.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              {q}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2 p-3 border-t border-white/10">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type a message…"
          maxLength={500}
          className="flex-1 bg-[var(--nl-bg-3)] border border-white/10 px-3 py-2.5 text-white font-body text-sm focus:outline-none focus:border-gold transition-colors duration-300"
          data-testid={`${testPrefix}-input`}
        />
        <button onClick={send} disabled={sending} className="bg-gold text-black px-4 flex items-center justify-center hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-50" data-testid={`${testPrefix}-send`}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}
